import * as React from 'react';
import { cn } from '@/utils/utils';
import { OVERLINE, type OverlineSize } from '@/components/ui/recipes';

// Lureo's type roles. Text that plays one of these roles uses its component (or, for an element of its own, the
// OVERLINE classes in recipes), never its own size, weight, case and tracking.


/** A small uppercase heading of the given size. */
export function Overline({ size = 'field', as: Tag = 'div', htmlFor, className, children }: { size?: OverlineSize; as?: 'div' | 'span' | 'h3' | 'h4' | 'label'; htmlFor?: string; className?: string; children: React.ReactNode }) {
  return (
    <Tag className={cn(OVERLINE[size], className)} {...(Tag === 'label' ? { htmlFor } : {})}>
      {children}
    </Tag>
  );
}

/** A panel section's title, spaced from the section above. */
export function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h3 className={cn(OVERLINE.section, 'mb-2.5 mt-4 first:mt-0', className)}>{children}</h3>;
}

/** A plain field label: sentence case in the medium weight, as the account, project and story settings pages label their forms. */
const PLAIN_LABEL = 'font-lureo-font-ui text-lureo-14 font-medium leading-5 text-lureo-ink-strong';
/** A small field label in an editor panel: sentence case, 12px medium, as the clip and Screen Message settings label their fields. */
const SMALL_LABEL = 'font-lureo-font-ui text-lureo-12 font-medium leading-4 text-lureo-ink-subtle';

/** A field's label; with htmlFor it is the field's <label>. The small capitals, plain on a settings page's form (account, project, story), or small in an editor panel's settings. */
export function FieldLabel({ children, mb = false, htmlFor, variant = 'overline', className }: { children: React.ReactNode; mb?: boolean; htmlFor?: string; variant?: 'overline' | 'plain' | 'small'; className?: string }) {
  const Tag = htmlFor ? 'label' : 'div';
  return (
    <Tag htmlFor={htmlFor} className={cn(variant === 'plain' ? PLAIN_LABEL : variant === 'small' ? SMALL_LABEL : OVERLINE.field, 'block', mb && 'mb-2', className)}>
      {children}
    </Tag>
  );
}

/** A group of cards or rows' heading. */
export function GroupTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h4 className={cn(OVERLINE.group, className)}>{children}</h4>;
}

/** What a field does, under it, in the prose face. */
export function FieldHint({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('mt-1.5 font-lureo-font-prose text-lureo-13 leading-[1.4] text-lureo-ink-subtle', className)}>{children}</div>;
}

/** What went wrong with a field or an action, and how to fix it, in the prose face. */
export function ErrorText({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn('font-lureo-font-prose text-lureo-12 leading-[1.4] text-lureo-danger-ink', className)}>{children}</p>;
}

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4';
/** Where a title sits or what it heads: the page or a card, a deep fill or a photo under its scrim (the hero band, the splash, the featured plan, a cover), a bright fill (the placeholder art), or a section of actions that delete. */
type TitleTone = 'default' | 'emphasis' | 'accent' | 'danger';
const TITLE_TONES = { default: 'text-lureo-ink-strong', emphasis: 'text-lureo-on-emphasis', accent: 'text-lureo-emphasis-strong', danger: 'text-lureo-danger-ink' } as const;
/** What every title takes from the set: its case, letter spacing and style. */
const TITLE = 'case-lureo-title-case tracking-lureo-title-tracking font-style-lureo-title-style';
/** What a display or page title also takes: its width, first-line indent, shadow and fill (title-fill, a gradient through its letters when the set has one). */
const BIG_TITLE = `${TITLE} font-width-lureo-title-width indent-lureo-title-indent text-shadow-lureo-title-shadow title-fill title-flicker`;

interface TitleProps {
  children: React.ReactNode;
  /** The heading level the document outline needs; the look stays the role's. */
  as?: HeadingLevel;
  tone?: TitleTone;
  className?: string;
}

/** An editor panel's title. */
export function PanelTitle({ children, as: Tag = 'h2', className }: Omit<TitleProps, 'tone'>) {
  return <Tag className={cn(TITLE, 'font-lureo-font-ui text-lureo-16 font-semibold leading-title-tight text-lureo-ink', className)}>{children}</Tag>;
}

/** The hero band's headline (the dashboard's greeting, the marketplace) or the not-found page's 404: the largest type. */
export function DisplayTitle({ children, as: Tag = 'h1', tone = 'emphasis', className }: TitleProps) {
  return <Tag className={cn(BIG_TITLE, 'font-lureo-font-display text-lureo-30 font-lureo-weight-heading leading-title-tight md:text-lureo-48 md:leading-title-none', TITLE_TONES[tone], className)}>{children}</Tag>;
}

/** A page's title, or a page section's (Stories, Your projects). */
export function PageTitle({ children, as: Tag = 'h1', tone = 'default', className }: TitleProps) {
  return <Tag className={cn(BIG_TITLE, 'title-rule font-lureo-font-display text-lureo-24 font-lureo-weight-heading leading-title-tight', TITLE_TONES[tone], className)}>{children}</Tag>;
}

/** A flow's step or its result (a publish flow, a confirmation), or a sign-in card's heading. */
export function FlowTitle({ children, as: Tag = 'h2', tone = 'default', className }: TitleProps) {
  return <Tag className={cn(TITLE, 'font-lureo-font-ui text-lureo-22 font-lureo-weight-subheading leading-title-snug', TITLE_TONES[tone], className)}>{children}</Tag>;
}

/** A card's title, or a dialog section's. */
export function CardTitle({ children, as: Tag = 'h2', tone = 'default', className }: TitleProps) {
  return <Tag className={cn(TITLE, 'font-lureo-font-ui text-lureo-18 font-lureo-weight-subheading leading-title-tight', TITLE_TONES[tone], className)}>{children}</Tag>;
}
